// src/components/PhotoTransform/StyleTransformModal.tsx
import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Grid,
   Typography,
   Box,
   CircularProgress,
   IconButton,
   Card,
   CardContent,
   CardMedia,
   Alert,
   Slider,
   MenuItem,
   Select,
   InputLabel,
   SelectChangeEvent,
   TextField,
   FormControl,
   Tooltip,
   Snackbar,
} from '@mui/material';
import { Close as CloseIcon, AutoFixHigh as WandIcon, Save as SaveIcon } from '@mui/icons-material';
import { StabilityImageStylePreset } from '../../services/stabilityImageTypes';
import { useUser } from '../../contexts/UserContext';
import { useImageTransform } from '../../hooks/useImageGen';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { doc } from 'firebase/firestore';
import { updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getStorage } from 'firebase/storage';
import { convertToJPEG } from '../../utils/imageUtils';

interface StyleTransformModalProps {
   open: boolean;
   onClose: () => void;
   photoUrl: string;
   photoId: string;
   onSave?: (photo: any) => void;
   // onTransformComplete: (transformedUrl: string) => void;
}

const styleOptions = [
   { value: 'realistic_image', label: 'Realistic Image' },
   { value: 'digital_illustration', label: 'Digital Illustration' },
   { value: 'vector_illustration', label: 'Vector Illustration' },
   //  { value: 'icon', label: 'Icon' },
   //  { value: 'logo_raster', label: 'Logo Raster' },
];

const realisticSubStylesOptions = [
   { value: 'b_and_w', label: 'B&W' },
   { value: 'enterprise', label: 'Enterprise' },
   { value: 'evening_light', label: 'Evening Light' },
   { value: 'faded_nostalgia', label: 'Faded Nostalgia' },
   { value: 'forest_life', label: 'Forest Life' },
   { value: 'hard_flash', label: 'Hard Flash' },
   { value: 'hdr', label: 'HDR' },
   { value: 'motion_blur', label: 'Motion Blur' },
   { value: 'mystic_naturalism', label: 'Mystic Naturalism' },
   { value: 'natural_light', label: 'Natural Light' },
   { value: 'natural_tones', label: 'Natural Tones' },
   { value: 'organic_calm', label: 'Organic Calm' },
   { value: 'real_life_glow', label: 'Real Life Glow' },
   { value: 'retro_realism', label: 'Retro Realism' },
   { value: 'retro_snapshot', label: 'Retro Snapshot' },
   { value: 'studio_portrait', label: 'Studio Portrait' },
   { value: 'urban_drama', label: 'Urban Drama' },
   { value: 'village_realism', label: 'Village Realism' },
   { value: 'warm_folk', label: 'Warm Folk' },
   { value: null, label: 'None' },
];

const digitalIllustrationSubStylesOptions = [
   { value: '2d_art_poster', label: '2D Art Poster' },
   { value: '2d_art_poster_2', label: '2D Art Poster 2' },
   { value: 'antiquarian', label: 'Antiquarian' },
   { value: 'bold_fantasy', label: 'Bold Fantasy' },
   { value: 'child_book', label: 'Child Book' },
   { value: 'child_books', label: 'Child Books' },
   { value: 'cover', label: 'Cover' },
   { value: 'crosshatch', label: 'Crosshatch' },
   { value: 'digital_engraving', label: 'Digital Engraving' },
   { value: 'engraving_color', label: 'Engraving Color' },
   { value: 'expressionism', label: 'Expressionism' },
   { value: 'freehand_details', label: 'Freehand Details' },
   { value: 'grain', label: 'Grain' },
   { value: 'grain_20', label: 'Grain 20' },
   { value: 'graphic_intensity', label: 'Graphic Intensity' },
   { value: 'hand_drawn', label: 'Hand Drawn' },
   { value: 'hand_drawn_outline', label: 'Hand Drawn Outline' },
   { value: 'handmade_3d', label: 'Handmade 3D' },
   { value: 'hard_comics', label: 'Hard Comics' },
   { value: 'infantile_sketch', label: 'Infantile Sketch' },
   { value: 'long_shadow', label: 'Long Shadow' },
   { value: 'modern_folk', label: 'Modern Folk' },
   { value: 'multicolor', label: 'Multicolor' },
   { value: 'neon_calm', label: 'Neon Calm' },
   { value: 'noir', label: 'Noir' },
   { value: 'nostalgic_pastel', label: 'Nostalgic Pastel' },
   { value: 'outline_details', label: 'Outline Details' },
   { value: 'pastel_gradient', label: 'Pastel Gradient' },
   { value: 'pastel_sketch', label: 'Pastel Sketch' },
   { value: 'pixel_art', label: 'Pixel Art' },
   { value: 'plastic', label: 'Plastic' },
   { value: 'pop_art', label: 'Pop Art' },
   { value: 'pop_renaissance', label: 'Pop Renaissance' },
   { value: 'seamless', label: 'Seamless' },
   { value: 'street_art', label: 'Street Art' },
   { value: 'tablet_sketch', label: 'Tablet Sketch' },
   { value: 'urban_glow', label: 'Urban Glow' },
   { value: 'urban_sketching', label: 'Urban Sketching' },
   { value: 'vanilla_dreams', label: 'Vanilla Dreams' },
   { value: 'young_adult_book', label: 'Young Adult Book' },
   { value: 'young_adult_book_2', label: 'Young Adult Book 2' },
   { value: null, label: 'None' },
];

const vectorIllustrationSubStylesOptions = [
   { value: 'bold_stroke', label: 'Bold Stroke' },
   { value: 'chemistry', label: 'Chemistry' },
   { value: 'colored_stencil', label: 'Colored Stencil' },
   { value: 'contour_pop_art', label: 'Contour Pop Art' },
   { value: 'cosmics', label: 'Cosmics' },
   { value: 'cutout', label: 'Cutout' },
   { value: 'depressive', label: 'Depressive' },
   { value: 'editorial', label: 'Editorial' },
   { value: 'emotional_flat', label: 'Emotional Flat' },
   { value: 'engraving', label: 'Engraving' },
   { value: 'infographical', label: 'Infographical' },
   { value: 'line_art', label: 'Line Art' },
   { value: 'line_circuit', label: 'Line Circuit' },
   { value: 'linocut', label: 'Linocut' },
   { value: 'marker_outline', label: 'Marker Outline' },
   { value: 'mosaic', label: 'Mosaic' },
   { value: 'naivector', label: 'Naivector' },
   { value: 'roundish_flat', label: 'Roundish Flat' },
   { value: 'seamless', label: 'Seamless' },
   { value: 'segmented_colors', label: 'Segmented Colors' },
   { value: 'sharp_contrast', label: 'Sharp Contrast' },
   { value: 'thin', label: 'Thin' },
   { value: 'vector_photo', label: 'Vector Photo' },
   { value: 'vivid_shapes', label: 'Vivid Shapes' },
   { value: null, label: 'None' },
];

const iconSubStylesOptions: any[] = [{ value: null, label: 'None' }];

const logoRasterSubStylesOptions = [
   { value: 'emblem_graffiti', label: 'Emblem Graffiti' },
   { value: 'emblem_pop_art', label: 'Emblem Pop Art' },
   { value: 'emblem_punk', label: 'Emblem Punk' },
   { value: 'emblem_stamp', label: 'Emblem Stamp' },
   { value: 'emblem_vintage', label: 'Emblem Vintage' },
   { value: null, label: 'None' },
];

const transformationTypeOptions = [
   { value: 'replace_background', label: 'Replace Background' },
   { value: 'transform_style', label: 'Transform Style' },
   { value: 'remove_background', label: 'Remove Background' },
   //  { value: 'generate_image', label: 'Generate Image' },
   //  { value: 'inpaint', label: 'Replace specific parts of the image' },
   //  { value: 'crisp', label: 'Enhance Image (sharpness and cleaner)' },
];

const StyleTransformModal: React.FC<StyleTransformModalProps> = ({
   open,
   onClose,
   photoUrl,
   photoId,
   onSave,
}) => {
   const { currentUser } = useUser();
   const [selectedStyle, setSelectedStyle] = useState<StabilityImageStylePreset | null>(null);
   const [transforming, setTransforming] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [previewUrl, setPreviewUrl] = useState<string | null>(null);
   const [transformationType, setTransformationType] = useState<string>('transform_style');
   const [stylePreset, setStylePreset] = useState<string>('');
   const [subStyle, setSubStyle] = useState<string>('');
   const [prompt, setPrompt] = useState<string>('');
   const [strength, setStrength] = useState<number>(0);
   const [isTransforming, setIsTransforming] = useState(false);
   const { imageToImageEditing, replaceBackground, removeBackground, generateImage, crispUpscale } =
      useImageTransform();
   const [transformedPhotos, setTransformedPhotos] = useState<any[]>([]);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
   const [snackbarMessage, setSnackbarMessage] = useState('');

   const handleTransformationTypeChange = (event: SelectChangeEvent<string>) => {
      setTransformationType(event.target.value);
   };

   const handleStyleChange = (event: SelectChangeEvent<string>) => {
      setStylePreset(event.target.value);
   };

   const handleSubStyleChange = (event: SelectChangeEvent<string>) => {
      setSubStyle(event.target.value);
   };

   const handleStrengthChange = (event: Event, newValue: number | number[]) => {
      setStrength(newValue as number);
   };

   const handleClose = () => {
      if (!transforming) {
         setSelectedStyle(null);
         setPreviewUrl(null);
         setError(null);
         onClose();
      }
   };

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   const handleTransformPhotos = async (options: {
      stylePreset: string;
      subStyle: string;
      prompt?: string;
      strength?: number | null;
      transformationType?: string | null;
   }) => {
      const { stylePreset, subStyle, prompt, strength, transformationType } = options;
      if (!photoUrl) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Please select a photo to transform');
         setSnackbarOpen(true);
         return;
      }

      setIsTransforming(true);
      setError(null);

      try {
         let transformedPhotos: any = null;

         if (transformationType === 'transform_style') {
            transformedPhotos = await imageToImageEditing({
               style: stylePreset,
               substyle: subStyle,
               prompt: prompt || '',
               strength: strength || 0,
               url: photoUrl,
            });
         } else if (transformationType === 'replace_background') {
            transformedPhotos = await replaceBackground({
               style: stylePreset,
               substyle: subStyle,
               prompt: prompt || '',
               url: photoUrl,
            });
         } else if (transformationType === 'remove_background') {
            transformedPhotos = await removeBackground(photoUrl);
         } else if (transformationType === 'generate_image') {
            transformedPhotos = await generateImage({
               prompt: prompt || '',
               style: stylePreset || '',
               substyle: subStyle || '',
            });
         } else if (transformationType === 'crisp') {
            transformedPhotos = await crispUpscale(photoUrl);
         }

         if (transformedPhotos?.images) {
            setTransformedPhotos((prev) => [
               ...transformedPhotos?.images?.map((photo: any) => ({
                  id: photo?.image_id,
                  originalPhotoId: photoId,
                  transformedUrl: photo?.url,
                  thumbnailUrl: photo?.url,
                  stylePreset: stylePreset,
                  prompt: prompt || '',
                  createdAt: new Date(transformedPhotos?.created),
                  type: photo?.type,
                  tripId: photo?.tripId,
                  eventId: photo?.eventId,
                  userId: photo?.userId,
               })),
            ]);
         }
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage(
            `An error occurred while transforming photos: ${err instanceof Error ? err.message : 'Unknown error'}`
         );
         setSnackbarOpen(true);
      } finally {
         setIsTransforming(false);
      }
   };

   // const handleSave = async (photoId: string) => {
   //    if (!currentUser?.uid || !photoId) {
   //       return;
   //    }

   //    try {
   //       // Create unique ID for the file
   //       const photo = transformedPhotos.find((photo: any) => photo.id === photoId);
   //       if (!photo) {
   //          setSnackbarSeverity('error');
   //          setSnackbarMessage('Photo not found');
   //          setSnackbarOpen(true);
   //          return;
   //       }
   //       const _photoId = `photo_${Date.now()}`;

   //       const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

   //       // Use the determined extension in the storage path
   //       const path = `user_photos/${currentUser?.uid}/${_photoId}.${extension}`;

   //       // Upload to Firebase Storage
   //       const storage = getStorage();
   //       const storageRef = ref(storage, path);

   //       // Upload file
   //       const uploadResult = await uploadBytes(storageRef, blob);

   //       const photoUrl = await getDownloadURL(storageRef);

   //       const newPhoto = {
   //          id: _photoId,
   //          url: photoUrl,
   //          timestamp: Date.now(),
   //          isTransformed: true,
   //          userId: currentUser?.uid,
   //          type: 'user_photo' ,
   //          createdAt: Timestamp.now(),
   //          updatedAt: Timestamp.now(),
   //          fileName: `${_photoId}.${extension}`,
   //          path: path,
   //       };

   //       await addDoc(collection(db, 'photos'), newPhoto);

   //       setSnackbarSeverity('success');
   //       setSnackbarMessage('Photo saved successfully');
   //       setSnackbarOpen(true);
   //    } catch (err) {
   //       setSnackbarSeverity('error');
   //       setSnackbarMessage('Failed to upload photo');
   //       setSnackbarOpen(true);
   //    } finally {
   //    }
   // };

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth='md'
         fullWidth
         aria-modal='true'
         disableEnforceFocus
         keepMounted
      >
         <DialogTitle>
            <Box display='flex' alignItems='center' justifyContent='space-between'>
               <Typography variant='h6'>Transform Photo with AI</Typography>
               <IconButton onClick={handleClose} disabled={transforming}>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent dividers>
            <Grid container spacing={3}>
               {/* Original image preview */}

               <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                     <Grid item xs={12} md={12}>
                        <Typography variant='subtitle1' gutterBottom>
                           Original Photo
                        </Typography>
                        <Box
                           sx={{
                              backgroundImage: `url(${photoUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              borderRadius: 1,
                              mb: 2,
                              height: 250,
                           }}
                        />
                     </Grid>
                     {/* Transformed image preview */}
                  </Grid>
               </Grid>

               <Grid item xs={12} md={6}>
                  <Typography variant='subtitle1' gutterBottom>
                     Transformation Options
                  </Typography>
                  <Grid container spacing={2}>
                     <Grid item xs={12}>
                        <FormControl fullWidth size='small'>
                           <InputLabel id='transformation-type-label'>
                              Transformation Type
                           </InputLabel>
                           <Select
                              labelId='transformation-type-label'
                              id='transformation-type'
                              value={transformationType}
                              label='Transformation Type'
                              onChange={handleTransformationTypeChange}
                              disabled={isTransforming}
                           >
                              {transformationTypeOptions.map((type) => (
                                 <MenuItem key={type.label} value={type.value}>
                                    {type.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>

                     {transformationType !== 'replace_background' &&
                        transformationType !== 'remove_background' &&
                        transformationType !== 'crisp' && (
                           <Grid item xs={12}>
                              <FormControl fullWidth size='small'>
                                 <InputLabel id='style-preset-label'>
                                    Style Preset (optional){' '}
                                 </InputLabel>
                                 <Select
                                    labelId='style-preset-label'
                                    id='style-preset'
                                    value={stylePreset}
                                    label='Style Preset (optional)'
                                    onChange={handleStyleChange}
                                    disabled={isTransforming}
                                 >
                                    {styleOptions.map((style) => (
                                       <MenuItem key={style.label} value={style.value}>
                                          {style.label}
                                       </MenuItem>
                                    ))}
                                 </Select>
                              </FormControl>
                           </Grid>
                        )}
                     {transformationType !== 'remove_background' &&
                        transformationType !== 'crisp' && (
                           <Grid item xs={12}>
                              <FormControl fullWidth size='small'>
                                 <InputLabel id='sub-style-label'>Sub-Style (optional)</InputLabel>
                                 <Select
                                    labelId='sub-style-label'
                                    id='sub-style'
                                    value={subStyle}
                                    label='Sub-Style (optional)'
                                    onChange={handleSubStyleChange}
                                    disabled={isTransforming}
                                 >
                                    {(stylePreset === 'realistic_image'
                                       ? realisticSubStylesOptions
                                       : stylePreset === 'digital_illustration'
                                         ? digitalIllustrationSubStylesOptions
                                         : stylePreset === 'vector_illustration'
                                           ? vectorIllustrationSubStylesOptions
                                           : stylePreset === 'icon'
                                             ? iconSubStylesOptions
                                             : stylePreset === 'logo_raster'
                                               ? logoRasterSubStylesOptions
                                               : []
                                    ).map((style) => (
                                       <MenuItem key={style.label} value={style.value}>
                                          {style.label}
                                       </MenuItem>
                                    ))}
                                 </Select>
                              </FormControl>
                           </Grid>
                        )}

                     {transformationType !== 'remove_background' &&
                        transformationType !== 'crisp' && (
                           <Grid item xs={12}>
                              <TextField
                                 fullWidth
                                 required
                                 label='Custom Prompt'
                                 placeholder='Enter additional guidance for the AI transformation...'
                                 multiline
                                 rows={2}
                                 value={prompt}
                                 onChange={(e) => setPrompt(e.target.value)}
                                 disabled={isTransforming}
                                 size='small'
                              />
                           </Grid>
                        )}

                     {transformationType !== 'remove_background' &&
                        transformationType !== 'replace_background' &&
                        transformationType !== 'crisp' &&
                        transformationType !== 'generate_image' && (
                           <Grid item xs={12}>
                              <Typography id='strength-slider-label'>
                                 Transformation Strength (optional) : {strength}
                              </Typography>

                              <Typography
                                 sx={{ fontSize: '12px' }}
                                 variant='caption'
                                 color='text.secondary'
                                 gutterBottom
                              >
                                 (Closer to 0 or 0 is more similar to the original image)
                              </Typography>

                              <Slider
                                 aria-labelledby='strength-slider-label'
                                 // value={strength}
                                 onChange={handleStrengthChange}
                                 value={strength}
                                 defaultValue={0}
                                 step={0.1}
                                 min={0}
                                 max={1}
                                 disabled={isTransforming}
                              />
                           </Grid>
                        )}
                  </Grid>
               </Grid>

               <Grid item xs={12} md={12}>
                  <Typography variant='subtitle1' gutterBottom>
                     Transformed Photo
                  </Typography>
                  {transformedPhotos.length == 0 && (
                     <Typography
                        sx={{ fontSize: '12px', wordWrap: 'break-word' }}
                        color='text.secondary'
                     >
                        No transformed photos yet
                     </Typography>
                  )}

                  <Grid container spacing={2}>
                     {transformedPhotos.map((photo: any) => {
                        return <TransformedPhoto key={photo?.id} photo={photo} onSave={onSave} />;
                     })}
                  </Grid>
               </Grid>

               <Snackbar
                  open={snackbarOpen}
                  autoHideDuration={6000}
                  onClose={handleCloseSnackbar}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
               >
                  <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                     {snackbarMessage}
                  </Alert>
               </Snackbar>
            </Grid>
         </DialogContent>

         <DialogActions>
            <Button onClick={handleClose} disabled={transforming}>
               Cancel
            </Button>
            <Button
               variant='contained'
               onClick={() =>
                  handleTransformPhotos({
                     stylePreset: stylePreset,
                     subStyle: subStyle,
                     prompt: prompt,
                     strength: strength,
                     transformationType: transformationType,
                  })
               }
               disabled={isTransforming || !photoUrl}
               startIcon={<WandIcon />}
            >
               {isTransforming ? 'Transforming...' : 'Transform Image'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

const TransformedPhoto = ({ photo, onSave }: { photo: any; onSave: any }) => {
   const [isSaving, setIsSaving] = useState(false);

   const handleSave = async () => {
      try {
         setIsSaving(true);
         await onSave(photo);
      } catch (err) {
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <Grid key={photo?.id} sx={{ height: 200, width: 250 }} item xs={12} sm={3} md={3}>
         <Box
            key={photo?.id}
            sx={{
               height: '100%',
               width: '100%',
               position: 'relative',
               borderRadius: 1,

               background: `url(${photo?.transformedUrl}) `,
               backgroundSize: 'cover',
               backgroundPosition: 'center',
            }}
         >
            {isSaving ? (
               <CircularProgress
                  sx={{ color: 'secondary.main', position: 'absolute', top: 10, right: 10 }}
               />
            ) : (
               <Tooltip title='Save' sx={{ position: 'absolute', top: 10, right: 10 }}>
                  <IconButton
                     onClick={() => {
                        if (onSave) {
                           handleSave();
                        }
                     }}
                     sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        backgroundColor: 'primary.main',
                        borderRadius: 1,
                        width: 30,
                        height: 30,
                        '&:hover': {
                           backgroundColor: 'primary.main',
                        },
                     }}
                  >
                     <SaveIcon sx={{ color: 'white', fontSize: 16 }} />
                  </IconButton>
               </Tooltip>
            )}
         </Box>
      </Grid>
   );
};

export default StyleTransformModal;
